import React, { createContext, useContext, useState, useEffect } from 'react'
import { apiClient } from '../api/client'
import type { Organization } from '../api/types'

interface OrgContextType {
  currentOrg: Organization | null
  organizations: Organization[]
  setCurrentOrg: (org: Organization) => void
  isLoading: boolean
}

const OrgContext = createContext<OrgContextType | undefined>(undefined)

export const useOrg = () => {
  const context = useContext(OrgContext)
  if (!context) {
    throw new Error('useOrg must be used within OrgProvider')
  }
  return context
}

interface OrgProviderProps {
  children: React.ReactNode
}

export const OrgProvider: React.FC<OrgProviderProps> = ({ children }) => {
  const [currentOrg, setCurrentOrgState] = useState<Organization | null>(null)
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Load organizations on mount
    const loadOrganizations = async () => {
      try {
        // TODO: Replace with actual API call when endpoint is ready
        const mockOrgs: Organization[] = [
          {
            id: 1,
            name: 'DarkHorse InfoSec',
            tier: 'hipaa',
            created_at: new Date().toISOString(),
          },
        ]
        setOrganizations(mockOrgs)

        // Set first org as default
        if (mockOrgs.length > 0) {
          const defaultOrg = mockOrgs[0]
          setCurrentOrgState(defaultOrg)
          apiClient.setOrgId(defaultOrg.id)
        }
      } catch (error) {
        console.error('Failed to load organizations:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadOrganizations()
  }, [])

  const setCurrentOrg = (org: Organization) => {
    setCurrentOrgState(org)
    apiClient.setOrgId(org.id)
  }

  return (
    <OrgContext.Provider
      value={{
        currentOrg,
        organizations,
        setCurrentOrg,
        isLoading,
      }}
    >
      {children}
    </OrgContext.Provider>
  )
}
